import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic } from '@tanstack/react-form';
import { useCallback, useState } from 'react';
import { type FileRejection } from 'react-dropzone';
import { z } from 'zod';

import { mutateAudienceCreate, uploadAudienceDomains } from '@/api/audience';
import AudienceBaseForm, { audienceBaseFormFields } from '@/app/audiences/AudienceBaseForm';
import { LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import Prompt from '@/components/Prompt';
import { ReturnToLink } from '@/components/ReturnToLink';
import { Link, Text } from '@/components/typography';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import useGlobalPaste from '@/hooks/useClipboard';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import AudienceCSVInput from './AudienceCSVInput';
import AudienceUpload from './AudienceUpload';
import { splitBySeparators } from './util';
import { isDomainValid } from './validator';

const MODE_DROPZONE = 'dropzone';
const MODE_TEXTAREA = 'textarea';

type Mode = 'dropzone' | 'textarea';

const MAX_FILE_SIZE = 6_291_456; // 6MB
const MAX_FILE_SIZE_TEXT = '6MB';

const TOKEN_SEPARATORS = [',', ' ', '\n'];

const defaultValues = {
  name: '',
  description: '',
  dataset_id: null as string | null,
  domains: [] as string[],
};

const CreateCustomAudience = () => {
  const [mode, setMode] = useState<Mode>(MODE_DROPZONE);
  const { pushNotification } = useNotification();
  const navigate = useInstanceNavigate();
  const getDomainsFieldSchema = () =>
    z
      .array(z.string().trim())
      .min(1, t`At least one domain is required`)
      .refine(
        (values) => values.every((value) => isDomainValid(value)),
        t`Values should be valid domain names (e.g. example.com), contain no spaces, and must be separated by commas`,
      );

  const datasetIdFieldSchema = z.string({ message: t`Audience file is required` });

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic(),
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          const { name, description, dataset_id, domains } = value;
          const isDropzoneMode = mode === MODE_DROPZONE;

          let audienceDatasetId: string;
          if (isDropzoneMode) {
            if (!dataset_id) {
              return {
                dataset_id: [{ message: t`Audience file is required` }],
              };
            }
            audienceDatasetId = dataset_id;
          } else {
            audienceDatasetId = await uploadAudienceDomains(domains);
          }

          await mutateAudienceCreate({
            type: 'upload',
            name,
            description,
            dataset_id: audienceDatasetId,
          });

          navigate(ROUTES.audiences.path, {
            state: {
              successMessage: t`Audience was successfully created.`,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        }
      },
    },
  });

  const switchToTextareaMode = useCallback(
    (domains?: string[]) => {
      // Remove upload field registration/meta so it cannot block textarea submissions.
      form.deleteField('dataset_id');
      if (domains) {
        form.setFieldValue('domains', domains);
      }
      setMode(MODE_TEXTAREA);
    },
    [form],
  );

  const handlePaste = useCallback(
    (pastedText: string) => {
      const isFileUploaded = form.state.values.dataset_id != null;
      const isInDropzoneMode = mode === MODE_DROPZONE;

      if (isInDropzoneMode && !isFileUploaded) {
        const pastedDomains = [...new Set(splitBySeparators(pastedText, TOKEN_SEPARATORS))].map(
          (value) => value.trim(),
        );

        switchToTextareaMode(pastedDomains);
      }
    },
    [form, mode, switchToTextareaMode],
  );

  useGlobalPaste(handlePaste);

  const setDatasetFieldErrors = (messages: { message: string }[]) => {
    form.setErrorMap({
      onChange: {
        fields: {
          dataset_id: messages,
        },
      },
    });
  };

  const handleFileAccepted = () => {
    setDatasetFieldErrors([]);
  };

  const handleFileRejection = (fileRejections: FileRejection[]) => {
    if (fileRejections.length === 0) {
      setDatasetFieldErrors([{ message: t`Audience file is not valid` }]);
      return;
    }

    const messages = fileRejections[0].errors.map((error) => {
      switch (error.code) {
        case 'file-too-large':
          return {
            message: t`File is larger than ${MAX_FILE_SIZE_TEXT}. Try removing any extra columns besides a single "Website" or "Domain" column to reduce the file size and try again.`,
          };
        case 'file-invalid-type':
          return {
            message: t`File is not a valid CSV or Excel file. Please try again.`,
          };
        default:
          return { message: t`Audience file is not valid` };
      }
    });

    setDatasetFieldErrors(messages);
  };

  const handleUploadError = (errorMessage: string) => {
    setDatasetFieldErrors([{ message: errorMessage }]);
  };

  return (
    <Page title={t`New Audience`} exampleAIQuestions={exampleAIQuestions('audience')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.Subscribe
            selector={(state) => ({
              isDirty: state.isDirty,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ isDirty, isSubmitting }) => (
              <Prompt
                message={t`Are you sure you want to exit without saving your new Audience?`}
                shouldPreventNavigation={isDirty && !isSubmitting}
              />
            )}
          </form.Subscribe>
          <div className="flex flex-col justify-center gap-6">
            <div className="flex justify-between gap-4">
              <ReturnToLink route={ROUTES.audiences.path} routeName={t`Audiences`} />
              <div className="flex items-center gap-4">
                <PendoPlaceholder />
                <LinkButton to={ROUTES.audiences.path}>
                  <Trans>Cancel</Trans>
                </LinkButton>
                <form.SubmitButton variant="primary">
                  <Trans>Save</Trans>
                </form.SubmitButton>
              </div>
            </div>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Panel title={t`Custom Audience Upload`} data-testid="custom-audience-upload-panel">
                  {mode === MODE_DROPZONE && (
                    <>
                      <div>
                        <Trans>
                          <Text variant="label">Note: </Text>
                          <Text variant="body1">
                            Your file must contain a column named "
                            <Text variant="label">Website</Text>" or "
                            <Text variant="label">Domain</Text>", all accounts must have a valid
                            domain to correctly match and fill must be a .csv or .xls format.
                          </Text>
                        </Trans>
                      </div>
                      <form.AppField
                        name="dataset_id"
                        validators={{
                          onSubmit: datasetIdFieldSchema,
                        }}
                      >
                        {(field) => (
                          <field.Field>
                            <FieldLabel htmlFor="custom-audience-upload">
                              <Trans>CSV, XLS, or Copy & Paste</Trans>
                            </FieldLabel>
                            <AudienceUpload
                              aria-invalid={
                                field.state.meta.isTouched && !field.state.meta.isValid
                                  ? true
                                  : undefined
                              }
                              disabled={isSubmitting}
                              maxFileSize={MAX_FILE_SIZE}
                              onChange={field.handleChange}
                              onBlur={field.handleBlur}
                              onFileAccepted={handleFileAccepted}
                              onFileRejected={handleFileRejection}
                              onUploadError={handleUploadError}
                            >
                              <Trans>
                                Drag and drop file,{' '}
                                <Link
                                  variant="body2"
                                  onClick={() => {
                                    switchToTextareaMode();
                                  }}
                                >
                                  Copy and Paste
                                </Link>
                              </Trans>
                            </AudienceUpload>
                            <field.Error />
                          </field.Field>
                        )}
                      </form.AppField>
                    </>
                  )}
                  {mode === MODE_TEXTAREA && (
                    <>
                      <div>
                        <Trans>
                          <Text variant="label">Note: </Text>
                          <Text variant="body1">
                            All accounts must have a valid domain to match correctly.
                          </Text>
                        </Trans>
                      </div>
                      <form.AppField
                        name="domains"
                        validators={{
                          onDynamic: mode === MODE_TEXTAREA ? getDomainsFieldSchema() : undefined,
                        }}
                      >
                        {(field) => (
                          <field.Field>
                            <FieldLabel htmlFor="custom-audience-domains">
                              <Trans>Copy & Paste or Input Text</Trans>
                            </FieldLabel>
                            <AudienceCSVInput
                              id="custom-audience-domains"
                              autoFocus
                              aria-invalid={
                                field.state.meta.isTouched && !field.state.meta.isValid
                                  ? true
                                  : undefined
                              }
                              placeholder={t`Enter domains separated by commas (e.g. example.com)`}
                              disabled={isSubmitting}
                              tokenSeparators={TOKEN_SEPARATORS}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={field.handleChange}
                            />
                            <field.Error />
                          </field.Field>
                        )}
                      </form.AppField>
                    </>
                  )}
                  <AudienceBaseForm
                    form={form}
                    fields={audienceBaseFormFields}
                    audience={undefined}
                    autoFocus={false}
                  />
                </Panel>
              )}
            </form.Subscribe>
          </div>
        </form.AppForm>
      </form>
    </Page>
  );
};

export default CreateCustomAudience;
